import { Injectable } from '@angular/core';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { RequestMetricResult } from '../../../shared/components/models/metrics/request-metric-result.enum';
import { ApplicationStateService } from '../../../shared/services/application-state.service';
import { ConfigurationService } from './configuration/configuration.service';
import { MetricsService } from '../../../shared/services/metrics.service';
import { RestApiService } from '../../../shared/services/rest-api.service';
import { CustomMetricsName } from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import { MetricsType } from '../../../shared/components/models/metrics/metrics-type.enum';
import { Offer } from '../../models/offer/offer.model';
import { BusinessParamName } from '../../../shared/models/request-offer/request-offer.enum';
import { UrlParamsService } from './url-params.service';
import { AppMode } from '../../../shared/models/app-mode.enum';
import { PrepareRegistrationService } from './prepare-registration.service';
import { RegistrationService } from './registration.service';
import {PostalCodesState} from '../../../shared/models/postal-codes-state';
import {CalculationService} from './calculation.service';
import {QueryParamName} from '../../enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class OfferMatchingService {

  private readonly CLASSNAME = 'OfferMatchingService';

  constructor(private stateService: ApplicationStateService,
              private configuration: ConfigurationService,
              private metricsService: MetricsService,
              private urlParamsService: UrlParamsService,
              private calculationService: CalculationService,
              private prepareRegistrationService: PrepareRegistrationService,
              private registrationService: RegistrationService,
              private api: RestApiService) { }

  get offerPackageName(): string {
    return this.stateService.offerPackageName;
  }

  get postalCodesState(): PostalCodesState {
    return this.stateService.state.postalCodes;
  }

  init(): void {
    this.initOfferPackageName();
  }

  matchOffer(offer: Offer): boolean {
    if (offer.packageName === this.offerPackageName) {
      this.stateService.saveNecessaryBusinessParamsFromOffer(offer.id);
      this.stateService.selectedOffer = offer;
      this.registrationService.resetOfferRegistrationData(true);
      this.prepareRegistrationService.sendPrepareRegistration(offer.id);
      this.updatePostalCodesState();
      return true;
    } else {
      return false;
    }
  }

  sendOfferMatchingMetric(result: boolean): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.OFFER_MATCHING_RESULT, MetricsType.PROCESS, result);
  }

  private updatePostalCodesState(): void {
    this.postalCodesState.ownerPostalCode = this.calculationService.ownerData.address.postalCode;
    if (this.calculationService.hasCoOwners) {
      this.postalCodesState.coOwnersPostalCodes = this.calculationService.ownerData.coOwners
          .map(coOwner => coOwner.address.postalCode);
    } else {
      this.postalCodesState.coOwnersPostalCodes = null;
    }
    this.stateService.updateApplicationState();
  }

  private initOfferPackageName() {
    const offerId = this.urlParamsService.getQueryParam(QueryParamName.OFFER_ID);
    const url = this.configuration.uiConfig.offerPackageNameEndpoint;

    const metricName = {
      className: this.CLASSNAME,
      methodName: 'initOfferPackageName'
    };

    if (isNotNullOrUndefined(offerId) && !isNotNullOrUndefined(this.offerPackageName)) {
      const params = { offerId: offerId };

      this.metricsService.sendRequestMetric({
        ...metricName,
        type: RequestMetricResult.SEND
      }, params);

      this.api.get(url, params)
        .then(resp => {
          if (resp.packageName && !resp.packageName.includes('AXA_DIRECT')) { // old package name fix
            this.stateService.offerPackageName = resp.packageName;
          } else {
            this.handleNotFoundPackageName();
          }

          this.metricsService.sendRequestMetric({
            ...metricName,
            type: RequestMetricResult.SUCCESS
          });
        })
        .catch(err => {
          this.handleNotFoundPackageName();
          this.metricsService.sendRequestMetric({
            ...metricName,
            type: RequestMetricResult.ERROR
          }, err);
          console.error('Unable to fetch package name for given offerId:', err);
        });
    }
  }

  private handleNotFoundPackageName(): void {
    this.stateService.mode = AppMode.RAW;
  }
}
